import { useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { diff as jsonDiff } from 'jsondiffpatch';
import { useHistory, useLocation } from 'react-router-dom';
import {
  fullPageRedirect,
  useIsEmbeddedWebView,
} from '@hungryroot/environment';
import { useExperimentFlags } from '@hungryroot/experiments';
import {
  log,
  logExceptionOnce,
  logUnknownErrorResponse,
} from '@hungryroot/o11y';
import {
  getApiV2CustomersOccasionEntityPreferencesPath,
  getApiV2OccasionPreferenceOptionsPath,
} from '@hungryroot/rest';
import { useIdentify } from '@hungryroot/rest-hooks';
import { HOMEPAGE_ROUTE } from '@hungryroot/routes';
import {
  getQuizToast,
  type QuizToastObj,
} from './components/QuizToast/quiz-toast';
import { serverPersist } from './data/server-persist';
import {
  getQuizStorageKeys,
  saveQuizToLocalStorage,
  type QuizStorageNamespace,
  DEFAULT_QUIZ_STORAGE_NAMESPACE,
} from './defs/localStorage';
import { type MiniQuizSequenceKey } from './defs/mini-quiz-routes';
import {
  type ProteinTagValue,
  type NonNullableQuiz,
  defaultQuiz,
} from './defs/quiz';
import { type RouteGroup } from './defs/route-groups';
import { type AllQuizRoutes } from './defs/routes';
import { useGuestSaveAndExitRoute } from './hooks/useGuestSaveAndExitRoute';
import { useQuizDataNormalizer } from './hooks/useQuizDataNormalizer';
import { useQuizLoader } from './hooks/useQuizLoader';
import { useQuizMutations } from './hooks/useQuizMutations';
import {
  type NavigationNode,
  useQuizNavigation,
} from './hooks/useQuizNavigation';
import {
  SKIP_QUIZ_INTRO_PARAM,
  removeSkipIntroParam,
} from './hooks/useQuizRoutes';
import { setSavedNavState } from './navigation/navigation';
import { getProgressSections } from './navigation/progress-sections';
import { type SequenceKey } from './navigation/resolveQuizSequence';
import { handleLowSodiumTags } from './utils/handleLowSodiumTags';
import { isSaveAndExitFromIntro } from './utils/isSaveAndExitFromIntro';

type Nullable<T> = { [K in keyof T]: T[K] | null };

export { type NonNullableQuiz };

export type QuizTagKeys = keyof Pick<
  Quiz,
  | 'dislike'
  | 'breakfast'
  | 'snacks'
  | 'sweets'
  | 'macroNutrient'
  | 'foodQualities'
  | 'foodFormats'
  | 'readyMade'
  | 'budget'
  | 'lunch'
  | 'dinner'
  | 'healthGoals'
>;

export type QuizBinaryTagKeys = keyof Pick<Quiz, 'cuisines' | 'flavors'>;

export type QuizTernaryTagKeys = keyof Pick<
  Quiz,
  'meats' | 'seafood' | 'plants'
>;

export type QuizFrequencyKeys = keyof Pick<
  Quiz,
  | 'breakfastFrequency'
  | 'lunchFrequency'
  | 'dinnerFrequency'
  | 'snacksFrequency'
  | 'sweetsFrequency'
>;

export type Quiz = Nullable<NonNullableQuiz>;

type FlatQuizTypes = {
  [K in keyof NonNullableQuiz]: NonNullableQuiz[K] extends (
    | number
    | string
    | ProteinTagValue
  )[]
    ? NonNullableQuiz[K][number]
    : NonNullableQuiz[K];
};

export type QuizTypes = FlatQuizTypes[keyof FlatQuizTypes];

const QUIZ_KEY = ['quiz'];

interface UseQuizResultNullQuiz {
  quiz: null;
  redirectPath?: undefined;
  quizToast: QuizToastObj | undefined;
}

interface UseQuizResultRedirect {
  quiz: null;
  redirectPath: AllQuizRoutes;
  quizToast: QuizToastObj | undefined;
}

export interface UseQuizResultWithQuiz<T> {
  quiz: Quiz;
  onSubmit(quiz: Quiz, continueOnSubmit?: boolean): void;
  navigationState: NavigationNode<string>;
  onQuestionAnswer<T extends keyof Quiz>(
    quiz: Quiz,
    question: T,
    value: Quiz[T]
  ): void;
  onSaveAndExit(quiz: Quiz): void;
  onQuizInit(): void;
  quizToast: QuizToastObj | undefined;
  screens: ReactNode[];
  sections: RouteGroup<T>[];
  // The sequence the visitor was routed onto. The full quiz has one sequence
  // (`base`); the mini quiz uses its own keys.
  sequenceKey?: SequenceKey | MiniQuizSequenceKey;
  isMiniQuiz: boolean;
  storageNamespace: QuizStorageNamespace;
}

type UseQuizResult<T> =
  | UseQuizResultNullQuiz
  | UseQuizResultWithQuiz<T>
  | UseQuizResultRedirect;

export function loadQuizFromLocalStorage(
  namespace: QuizStorageNamespace = DEFAULT_QUIZ_STORAGE_NAMESPACE
): Quiz | null {
  const value = window.localStorage.getItem(
    getQuizStorageKeys(namespace).local
  );
  if (value === null) {
    return null;
  }

  return JSON.parse(value);
}

function getCustomerId(
  customerQuery: ReturnType<typeof useIdentify>
): number | null {
  const { customerData } = customerQuery;
  if (customerData === undefined) {
    return null;
  }

  return customerData.id;
}

interface Props {
  onQuestionSubmit?(params: {
    nextNavigationState: NavigationNode;
    quiz: Quiz;
  }): void;
  onQuizComplete: () => void;
}

export function useQuiz({ onQuizComplete, onQuestionSubmit }: Props) {
  const customerSelf = useIdentify();
  const query = useQuizLoader();
  const history = useHistory();
  const { pathname, search: searchParams } = useLocation();
  const quizMutations = useQuizMutations();
  const isEmbeddedWebView = useIsEmbeddedWebView();
  const { isLoading: isExpLoading, planRecommenderExp234 } =
    useExperimentFlags(234);
  const isPlanRecommenderV1Variant =
    !isExpLoading &&
    planRecommenderExp234.variant >= 1 &&
    planRecommenderExp234.variant <= 6;
  // A mobile freemium guest leaves the quiz for the discover feed, where
  // their guest experience lives; everyone else returns to the homepage.
  const saveAndExitRoute = useGuestSaveAndExitRoute({
    fallbackRoute: HOMEPAGE_ROUTE,
  });

  const persistQuiz = usePersistQuizMutation();
  const [persistedQuiz, setPersistedQuiz] = useState(query.data);
  const [inMemoryQuiz, setInMemoryQuiz] = useState<Quiz | undefined>(() => {
    if (query.data === undefined) {
      return undefined;
    }

    return query.data === null ? defaultQuiz : query.data;
  });

  const [quizToast, setQuizToast] = useState<QuizToastObj | undefined>();

  if (persistedQuiz !== query.data) {
    setPersistedQuiz(query.data);
    setInMemoryQuiz(query.data === null ? defaultQuiz : query.data);
  }

  // Added some logging to quantify the amount of users that will come across:
  // https://github.com/hungryroot/web/pull/3126.
  if (
    persistedQuiz &&
    query?.data &&
    persistedQuiz === query.data &&
    inMemoryQuiz === undefined
  ) {
    logExceptionOnce(
      'In-memory quiz likely to stay `undefined`, resulting in a blank page.',
      {
        key: 'useQuiz.inMemoryQuiz.staying.undefined',
        pathname,
        isEmbeddedWebView,
      }
    );
  }

  const navigationState = useQuizNavigation({
    loc: pathname,
    searchParams,
    query,
    quiz: inMemoryQuiz,
  });

  const quizDataNormalizer = useQuizDataNormalizer();

  if (
    inMemoryQuiz === undefined ||
    navigationState === undefined ||
    quizMutations === undefined
  ) {
    return {
      quiz: null,
      quizToast: undefined,
    };
  }

  const progressSections = getProgressSections(navigationState.routeEntries);

  const { redirect: redirectPath } = navigationState;

  // Remove skip-intro param after it's been used for initial route calculation
  const queryParams = new URLSearchParams(searchParams);
  if (queryParams.has(SKIP_QUIZ_INTRO_PARAM)) {
    const cleanSearch = removeSkipIntroParam(searchParams);
    history.replace({ pathname, search: cleanSearch });
  }

  if (redirectPath !== undefined) {
    return {
      quiz: null,
      redirectPath,
      quizToast: undefined,
    };
  }

  const { navigation, routes: quizRoutes, sequenceKey } = navigationState;
  const { path: currentQuizPath } = navigation;
  setSavedNavState(currentQuizPath);

  const customerId = getCustomerId(customerSelf);

  const onQuestionAnswer = <K extends keyof Quiz>(
    q: Quiz,
    question: K,
    value: NonNullableQuiz[K]
  ) => {
    const computedValue = handleLowSodiumTags(
      q,
      question,
      value
    ) as typeof value;
    const nextQuiz = quizMutations.onQuestionAnswer(q, question, computedValue);
    const quizToast = getQuizToast(nextQuiz, q, {
      goals: true,
    });
    setQuizToast(quizToast);
    setInMemoryQuiz(nextQuiz);

    return nextQuiz;
  };

  const result: UseQuizResult<Exclude<typeof quizRoutes, undefined>[number]> = {
    sections: progressSections,
    screens: [],
    quiz: inMemoryQuiz,
    navigationState: navigation,
    quizToast,
    onQuestionAnswer,
    sequenceKey,
    isMiniQuiz: false,
    storageNamespace: DEFAULT_QUIZ_STORAGE_NAMESPACE,
    onSubmit(
      nextQuiz: Quiz,
      continueOnSubmit: boolean = true,
      onSubmitAnalytics?: () => void
    ) {
      const { next } = navigation;

      // Exp 234 variants 1–6 used to overwrite these answers with a
      // Some/None derivation on the final submit, which dropped All.
      // The page is shown to everyone now; keep the answered values and
      // record when a previously-overwritten enrollment comes through.
      if (isPlanRecommenderV1Variant && next === null) {
        log('Persisting answered meal frequencies for an Exp 234 variant', {
          key: 'useQuiz.exp234MealFrequencies',
          variant: planRecommenderExp234.variant,
          breakfastFrequency: nextQuiz.breakfastFrequency,
          lunchFrequency: nextQuiz.lunchFrequency,
          dinnerFrequency: nextQuiz.dinnerFrequency,
          snacksFrequency: nextQuiz.snacksFrequency,
          sweetsFrequency: nextQuiz.sweetsFrequency,
        });
      }

      const normalizedNextQuiz = quizDataNormalizer.normalizeData(nextQuiz);

      const mutationParams: QuizMutationParams =
        customerId === null
          ? {
              quiz: normalizedNextQuiz,
              customerId,
            }
          : {
              quiz: normalizedNextQuiz,
              customerId,
              persistedQuiz: persistedQuiz as Quiz,
            };

      persistQuiz.mutate(mutationParams, {
        onSuccess() {
          const nextNavigationState = navigation;
          const { next } = nextNavigationState;

          onQuestionSubmit?.({
            nextNavigationState,
            quiz: normalizedNextQuiz,
          });

          if (next === null) {
            onQuizComplete();
            return;
          }

          if (continueOnSubmit) {
            history.push(next);
          }
        },
      });

      onSubmitAnalytics?.();

      const quizToast = getQuizToast(nextQuiz, inMemoryQuiz);
      setQuizToast(quizToast);
    },
    onSaveAndExit(nextQuiz: Quiz) {
      if (isSaveAndExitFromIntro(currentQuizPath)) {
        return fullPageRedirect(saveAndExitRoute);
      }

      const normalizedNextQuiz = quizDataNormalizer.normalizeData(nextQuiz);

      const mutationParams: QuizMutationParams =
        customerId === null
          ? {
              quiz: normalizedNextQuiz,
              customerId,
            }
          : {
              quiz: normalizedNextQuiz,
              customerId,
              persistedQuiz: persistedQuiz as Quiz,
            };
      persistQuiz.mutate(mutationParams, {
        onSuccess() {
          fullPageRedirect(saveAndExitRoute);
        },
      });
    },
    onQuizInit() {
      // Note, we do not need to normalize quiz data here for meal type questions because this is only used to initialize with default quiz data.
      const nextQuiz = { ...defaultQuiz };

      const mutationParams: QuizMutationParams =
        customerId === null
          ? {
              quiz: nextQuiz,
              customerId,
            }
          : {
              quiz: nextQuiz,
              customerId,
              persistedQuiz: persistedQuiz as Quiz,
            };

      persistQuiz.mutate(mutationParams, {
        onSuccess() {
          const nextNavigationState = navigation;
          const { next } = nextNavigationState;
          if (next === null) {
            return;
          }
          history.push(next);
        },
      });
    },
  };

  return result;
}

/**
 * For EXP-158, 815 and 811 need to have the same values (grass fed and pasture raised)
 * However, only grass fed is shown in the quiz.
 * This means:
 *  - if grass fed is selected, and pasture raised is not, add pasture raised
 *  - if grass fed is not selected, and pasture raised is, remove pasture raised
 */
function handleFoodQualitiesForQuiz(quiz: Quiz) {
  // If we have a loaded quiz that does not have foodQualities, return it as is
  if (quiz.foodQualities === undefined) return quiz;

  let updatedFoodQualities: number[] | null = quiz.foodQualities;
  if (
    quiz.foodQualities !== null &&
    quiz.foodQualities?.includes(815) &&
    !quiz.foodQualities?.includes(811)
  ) {
    // add pasture raised if grass fed meat is selected
    updatedFoodQualities = [...quiz.foodQualities, 811];
  }
  if (
    quiz.foodQualities !== null &&
    quiz.foodQualities !== undefined &&
    !quiz.foodQualities?.includes(815)
  ) {
    // remove pasture raised if grass fed meat is not selected
    updatedFoodQualities =
      quiz.foodQualities?.filter((id) => id !== 811) ?? null;
  }

  return {
    ...quiz,
    foodQualities: updatedFoodQualities,
  } as Quiz;
}

type QuizMutationParams =
  | {
      quiz: Quiz;
      customerId: null;
    }
  | {
      quiz: Quiz;
      customerId: number;
      persistedQuiz: Quiz;
    };

function usePersistQuizMutation() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: async (params: QuizMutationParams) => {
      const { quiz, customerId } = params;

      if (customerId === null) {
        // customer is not logged in, persist changes locally
        saveQuizToLocalStorage(handleFoodQualitiesForQuiz(quiz));
        return {
          diff: true,
        };
      }

      const { persistedQuiz } = params;
      const diff = jsonDiff(quiz, persistedQuiz);

      if (diff === undefined) {
        return {
          diff: false,
        };
      }

      await serverPersist(customerId, handleFoodQualitiesForQuiz(quiz), {
        originalQuiz: handleFoodQualitiesForQuiz(persistedQuiz),
      });
      return {
        diff: true,
      };
    },
    onSuccess(data: { diff: boolean }, params: QuizMutationParams) {
      const { diff } = data;
      const { customerId } = params;

      if (customerId === null) {
        // only invalidate local quiz if user is anonymous
        client.invalidateQueries({ queryKey: [QUIZ_KEY, 'local'] });
        return;
      }

      if (diff === false) {
        return;
      }

      client.invalidateQueries({ queryKey: [QUIZ_KEY, 'api'] });

      // Query invalidation for exp 175
      // Invalidate customer dietary restrictions query. Result passed as a param in occasion preference option query.
      client.invalidateQueries({ queryKey: ['customer-dietary-restrictions'] });
      // Invalidate occasion preference option query. Results used to populate data on meal type pages
      client.invalidateQueries({
        queryKey: [getApiV2OccasionPreferenceOptionsPath()],
        exact: false, // this ensures all queries starting with this path are invalidated regardless of parameters
      });
      // Invalidate customer occasion preferences. Results used to populate selected items on meal type pages
      client.invalidateQueries({
        queryKey: [getApiV2CustomersOccasionEntityPreferencesPath()],
      });
    },
    onError(error: unknown, variables: QuizMutationParams) {
      logUnknownErrorResponse('Failed to persist quiz data to server', {
        key: 'quiz.serverPersist',
        error: error instanceof Error ? error.message : String(error),
        customerId: variables.customerId,
      });
    },
  });
}
